import React, { useState, useEffect, useRef } from "react";
import axios from "../../AxiosInstance"; // Adjust the import based on your project structure
import toast from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { BASE_URL } from "../../config/BaseUrls";
import { useNavigate } from "react-router-dom";
import { Card, Form, InputGroup, Modal } from "react-bootstrap";
import { resolveBookImageUrl } from "../../utils/urlHelpers";

const SuggestiveBookPage = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const timeoutRef = useRef(null);
  const [modalData, setModalDate] = useState({
    image: null,
    title: "",
    writer: "",
    genre: "",
    aboutBook: "",
  });
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchSuggestions = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }
    setModalDate({ showImage: false });
    setLoading(true);
    try {
      const res = await axios.get(
        `api/search?query=${encodeURIComponent(searchQuery)}`
      );
      // Map the API response to match your data structure
      const formattedBooks = res.data?.books?.map((book) => ({
        id: book.EAN,
        title: book["Title Derived"],
        author: book["First Contributor Name"],
        format: book["Product Form Description"],
        publisher: book["Publisher Imprint"],
        genre: book["1st BISAC Major Description"],
        pageNumber: book["Page Number"],
        aboutBook: book["AboutBook"],
        image: book.image,
      }));
      setSuggestions(formattedBooks);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Search error:", err);
      setSuggestions([]);
    }
    setLoading(false);
  };
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };
  const [headings, setHeadings] = useState([]);
  const [headingTitle, setHeadingTitle] = useState("");
  const [books, setBooks] = useState([]);
  const [newBookTitle, setNewBookTitle] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [rating, setRating] = useState(5);
  const [selectedHeading, setSelectedHeading] = useState("");
  const fileInputRef = useRef(null); // File input ke liye reference
  const [preview, setPreview] = useState(null);

  const [dragActive, setDragActive] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false); // Remove highlight
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setCoverImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setCoverImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    fetchHeadings();
  }, []);
  const fetchHeadings = async () => {
    try {
      const response = await axios.get("api/v1/review/getoffelliotitle");
      setHeadings(response.data);
    } catch (error) {
      console.error(
        "Error fetching headings:",
        error.response?.data || error.message
      );
    }
  };
  const handleAddHeading = async () => {
    if (!headingTitle.trim()) {
      // Agar headingTitle empty ya sirf spaces hain
      toast.error("Please enter a Title before Add Title.");
      return;
    }

    try {
      const response = await axios.post("api/v1/review/addheadingpage", {
        title: headingTitle,
      });
      setHeadings([...headings, response.data]);
      setHeadingTitle(""); // Input field ko clear karna
      toast.success(response.data.message);
    } catch (error) {
      console.error(
        "❌ Axios Error:",
        error.response?.data?.message || error.message
      );
      toast.error(error.response?.data?.message || "Something Went wrong");
    }
  };

  const handleAddBook = async (e) => {
    e.preventDefault();

    // ✅ Create a simple JSON object instead of FormData
    const bookData = {
      coverImage: modalData?.showImage
        ? modalData?.link
        : `${selectedBook?.image}`, // should be a base64 string or image URL
      ean: selectedBook?.id || "", // or extract from file name if needed
      rating,
      title: selectedBook?.title || "",
      author: selectedBook?.author || "",
      genre: selectedBook?.genre || "",
      aboutBook: selectedBook?.aboutBook || "",
      name: query,
    };
    // ✅ Required fields
    const requiredFields = {
      coverImage: "Cover Image",
      title: "Title",
      author: "Author",
      genre: "Genre",
    };

    // ✅ Find missing fields
    const missingFields = Object.entries(requiredFields)
      .filter(([key]) => !bookData[key] || bookData[key].trim?.() === "")
      .map(([, label]) => label);

    // ✅ Show error if any field is missing
    if (missingFields.length > 0) {
      return toast.error(
        `Please fill in the following fields: ${missingFields.join(", ")}.`
      );
    }
    try {
      const response = await axios.post(
        "api/v1/review/addbookcoverpage",
        bookData, // <-- Send JSON
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setBooks([...books, response.data]);
      setNewBookTitle("");
      setCoverImage(null);
      setRating(5);
      setQuery("");
      setShowSuggestions(false);
      fetchAllBooks(headings);
      setSelectedHeading("");
      setPreview("");
      toast.success(response.data.message);

      if (fileInputRef.current) fileInputRef.current.value = null;
    } catch (error) {
      console.error("Error uploading book:", error);
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  const addBookManually = async () => {
    setSelectedBook({
      title: modalData.title,
      author: modalData.writer,
      genre: modalData.genre,
      aboutBook: modalData.aboutBook || "",
    });

    const formData = new FormData();

    // Append text fields
    formData.append("title", modalData.title);
    formData.append("writer", modalData.writer);
    formData.append("genre", modalData.genre);
    formData.append("description", modalData.aboutBook || "");

    // Append image if exists
    if (modalData.image) {
      formData.append("image", modalData.image);
    }

    try {
      const response = await axios.post("/api/v1/books/manual-add", formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Important for file uploads
        },
      });
      console.log(response, "ddd");

      setModalDate({
        image: null,
        title: "",
        writer: "",
        genre: "",
        aboutBook: "",
        showImage: true,
        link: response.data?.data.coverImage, // Assuming API returns coverImage in response
      });

      handleCloseB();
      return response.data;
    } catch (error) {
      console.error("Error adding book:", error);

      // Enhanced error handling
      let errorMessage = "Failed to add book";
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || errorMessage;

        // Handle validation errors if available
        if (error.response.data?.errors) {
          errorMessage = Object.values(error.response.data.errors).join("\n");
        }
      } else if (error.request) {
        // Request was made but no response
        errorMessage = "Network error - please check your connection";
      }

      // Show pretty toast notification
      toast.error(errorMessage);
    }
  };
  const handleImageUpload = (event) => {
    const file = event.target.files[0]; // یوزر کی منتخب کردہ فائل
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setSelectedImagemodel(fileURL);
      setImageName(file.name);

      // لوکل اسٹوریج میں تصویر اور اس کا نام محفوظ کریں
      localStorage.setItem("uploadedImagemodelaaaa", fileURL);
      localStorage.setItem("uploadedImageNamenew", file.name);
      setModalDate({ ...modalData, image: file });
    }
  };
  const [showB, setShowB] = useState(false); // For signup modal
  const handleCloseB = () => {
    setShowB(false);
  };
  const handleShowB = () => {
    setShowB(true); // Open  modal
  };
  const [selectedImagemodel, setSelectedImagemodel] = useState(null);
  const [imageName, setImageName] = useState("");
  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalDate((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  useEffect(() => {
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);
  const [loading, setLoading] = useState(true);
  const [heading, setHeading] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);

  const [booksByHeading, setBooksByHeading] = useState({});
  const [slidesToShowConfig, setSlidesToShowConfig] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    fetchHeadingsBooks();
  }, []);

  const fetchHeadingsBooks = async () => {
    try {
      const response = await axios.get("api/v1/review/getoffelliotitle");
      setHeading(response.data);
      await fetchAllBooks(response.data); // Automatically fetch books for all headings
      // setBookCount(response.data.length);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching headings:", error);
    }
  };

  const fetchAllBooks = async (headings) => {
    try {
      // const allBooks = {};
      // const slidesConfig = {};

      // for (const heading of headings) {
      //   const response = await axios.get(`api/v1/review/${heading._id}`);
      //   allBooks[heading._id] = response.data; // Storing books under their respective heading ID
      //   const bookCount = response.data.length;
      //   slidesConfig[heading._id] = bookCount;
      // }
      const response = await axios.get(`/api/v1/admin/getAllBookCoverOfellio`);
      console.log(response?.data?.books.length, "books by heading");

      setBooksByHeading(response?.data?.books);

      setSlidesToShowConfig(response?.data?.books?.length);
    } catch (error) {
      console.error("Error fetching books:", error);
    }
  };
  const StarRating = ({ rating }) => {
    const totalStars = 5;

    return (
      <div>
        {Array.from({ length: totalStars }, (_, index) => (
          <span
            key={index}
            style={{
              fontSize: "21px",
              color: index < rating ? "#ffc107" : "#ddd", // Warning color for filled stars, light grey for empty stars
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };
  // New State to Save Clicked Book

  const handleBookClick = (book) => {
    // Save the complete book data to localStorage
    setSelectedBook(book);
    setQuery(book.title);
    setShowSuggestions(false);
    // Optional: Trigger any parent component callback or state update
  };
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    // Create a new array and reorder the books
    const updatedBooks = Array.from(booksByHeading);
    const [moved] = updatedBooks.splice(result.source.index, 1);
    updatedBooks.splice(result.destination.index, 0, moved);

    // Update local state
    setBooksByHeading(updatedBooks);

    // Prepare data for backend
    const newOrder = updatedBooks.map((book, index) => ({
      id: book._id,
      order: index,
    }));

    try {
      await axios.post("api/v1/review/updatedOrder", newOrder);
    } catch (error) {
      console.error("Error updating book order:", error);
    }
  };

  const handleDeleteBook = async (bookId, headingId) => {
    try {
      const response = await axios.delete(`api/v1/review/delete/${bookId}`, {
        headers: {
          "Content-Type": "application/json",
          // Uncomment and add token if authentication is required
          // Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.status === 200) {
        toast.success("Book deleted successfully!");
        // Refetch books to update the UI
        await fetchAllBooks(headings);
      }
    } catch (error) {
      console.error("Error deleting book:", error);
      toast.error(error.response?.data?.message || "Failed to delete book");
    }
  };
  return (
    <>
      <div className="d-md-flex gap-5  ">
        <Sidebar />
        <div>
          <div className="px-md-0 px-3 mt-md-0 mt-4 ">
            <h2 className="text-center mt-4">Ofellio's Suggested </h2>
            <form className="addstoremain mt-3">
              <div className="d-sm-flex gap-2">
                <div className="Storenmae">Title:</div>
                <input
                  value={headingTitle}
                  onChange={(e) => setHeadingTitle(e.target.value)}
                  placeholder="Enter Title"
                  className="inputborder"
                />
              </div>
              <div className="btnstore btnstwidth mt-2">
                <button
                  // onClick={handleAddHeading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleShowB();
                  }}
                  className="text-white subbtnmeet border-0  fw600 rounded-1"
                  style={{
                    width: "110px",
                    height: "36px",
                    fontSize: "14px",
                    backgroundColor: "#62C0C7",
                  }}
                >
                  Add Manually
                </button>
              </div>

              {/* <div className="d-sm-flex gap-2 mt-3">
                <div className="Storenmae">Select Title:</div>

                <select
                  onChange={(e) => setSelectedHeading(e.target.value)}
                  value={selectedHeading}
                  className="inputborder"
                  style={{ border: "1.5px solid black" }}
                >
                  <option value="">Select Title</option>
                  {headings.map((heading) => (
                    <option key={heading._id} value={heading._id}>
                      {heading.title}
                    </option>
                  ))}
                </select>
              </div> */}

              <div
                className="relative mlzerosugpg "
                style={{ width: "350px", marginLeft: "128px" }}
              >
                <InputGroup>
                  <Form.Control
                    type="text"
                    // placeholder="Search book..."
                    placeholder={"Search book"}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowSuggestions(false), 200)
                    }
                    style={{
                      height: "41px",
                      border: "2px solid #3C3C3C",
                      borderRadius: "4px",
                    }}
                  />
                </InputGroup>

                {showSuggestions && (
                  <div
                    style={{
                      position: "absolute",
                      width: "100%",
                      maxHeight: "300px",
                      maxWidth: "350px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      zIndex: 1000,
                      marginTop: "5px",
                    }}
                  >
                    {loading ? (
                      <div className="p-2 text-muted">Loading...</div>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((book) => (
                        <div
                          key={book.id}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={() => handleBookClick(book)}
                        >
                          {book.image && (
                            <img
                              src={
                                resolveBookImageUrl(book.image)
                              }
                              alt={book.title}
                              style={{
                                width: "30px",
                                height: "45px",
                                objectFit: "cover",
                                marginRight: "10px",
                              }}
                            />
                          )}
                          <div>
                            <div style={{ fontWeight: "bold" }}>
                              {book.title}
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#666" }}>
                              {book.author} • {book.publisher}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : query ? (
                      <div className="p-2 text-muted">No results found</div>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="mt-xxl-4 mt-lg-3 mt-sm-3  mt-4 flex justify-content-center align-items-end flex-col">
                <div className="">
                  <input
                    type="text"
                    className="form-control border-0 widmeetupin fw-bold fs-5 ftzevtt rounded-1 placeholder-login"
                    id="text"
                    required
                    value={selectedBook?.title || ""}
                    style={{
                      // backgroundColor: "#EAEAEA",
                      background:
                        "repeating-linear-gradient(45deg, #EAEAEA, #EAEAEA 10px, #d6d6d6 10px, #d6d6d6 11px)",

                      width: "350px",
                      height: "38px",
                    }}
                    readOnly
                  />
                </div>

                <div className="mt-4">
                  <input
                    type="text"
                    className="form-control border-0 widmeetupin fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="text"
                    required
                    value={selectedBook?.author || ""}
                    readOnly
                    style={{
                      // backgroundColor: "#EAEAEA",
                      background:
                        "repeating-linear-gradient(45deg, #EAEAEA, #EAEAEA 10px, #d6d6d6 10px, #d6d6d6 11px)",

                      width: "350px",
                      height: "38px",
                    }}
                  />
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    className="form-control border-0 widmeetupin fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="text"
                    required
                    value={selectedBook?.genre || ""}
                    readOnly
                    style={{
                      // backgroundColor: "#EAEAEA",
                      background:
                        "repeating-linear-gradient(45deg, #EAEAEA, #EAEAEA 10px, #d6d6d6 10px, #d6d6d6 11px)",

                      width: "350px",
                      height: "38px",
                    }}
                  />
                </div>
              </div>
              <div className="d-sm-flex gap-3 mt-2">
                {/* 
              <input
                onChange={(e) => setCoverImage(e.target.files[0])} // <- Change here
                placeholder="Cover Image URL"
                type="file"
                className="inputborder border-0"
                ref={fileInputRef}
                accept="image/*"
              /> */}
                <div
                  className=" widimgamsdr  d-flex d-lg-block w-100 justify-content-end"
                  style={{
                    // backgroundColor: "#EAEAEA",
                    background:
                      "repeating-linear-gradient(45deg, #EAEAEA, #EAEAEA 10px, #d6d6d6 10px, #d6d6d6 12px)",

                    maxWidth: "200px",
                    height: "200px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "end",
                    position: "relative",
                    // cursor: "pointer",
                    overflow: "hidden", // Ensures th
                  }}
                >
                  {selectedBook?.image || modalData?.showImage ? (
                    <img
                      // onClick={() => handleSuggestionClickName(selectedBook)}
                      src={
                        modalData?.showImage
                          ? selectedImagemodel
                          : resolveBookImageUrl(selectedBook?.image) // ✅ plain filename
                      }
                      alt={
                        modalData?.showImage
                          ? selectedImagemodel
                          : resolveBookImageUrl(selectedBook?.image)
                      }
                      style={{
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        objectFit: "cover", // Ensures the image covers the container without distortion
                      }}
                      //   style={{ width: "118px", height: "118px" }} // Set dimensions as needed
                    />
                  ) : (
                    <div className="text-muted p-3 text-center"></div>
                  )}
                </div>
              </div>

              <div className="btnstore btnstwidth mt-3">
                <button
                  onClick={handleAddBook}
                  className="text-white subbtnmeet border-0  fw600 rounded-1"
                  style={{
                    width: "110px",
                    height: "36px",
                    fontSize: "14px",
                    backgroundColor: "#62C0C7",
                  }}
                >
                  Add Book
                </button>
              </div>
            </form>
          </div>
          <div className="mx-md-4 mx-3 my-5">
            {" "}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="booksGrid" direction="vertical">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="d-flex flex-wrap my-4 gap-4"
                    style={{ minHeight: "100px" }} // ensure droppable area always exists
                  >
                    {booksByHeading?.length > 0 ? (
                      booksByHeading?.map((book, index) => (
                        <Draggable
                          key={book._id}
                          draggableId={book._id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <Card className="border-0 align-items-center position-relative">
                                <button
                                  className="position-absolute top-0 end-0 m-2 btn btn-sm btn-danger"
                                  style={{
                                    borderRadius: "50%",
                                    width: "24px",
                                    height: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0,
                                    fontSize: "16px",
                                    lineHeight: "1",
                                  }}
                                  onClick={() => handleDeleteBook(book._id)}
                                  title="Delete Book"
                                >
                                  &times;
                                </button>

                                <Card.Img
                                  className="imgsizffe"
                                  variant="top"
                                  src={
                                    resolveBookImageUrl(book?.coverImage)
                                  }
                                  alt="suggestivebook"
                                  style={{
                                    width: "110px",
                                    height: "160px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleBookClick(book)}
                                />

                                <Card.Body className="p-0 mt-0">
                                  <div className="mx-2">
                                    <span style={{ fontSize: "21px" }}>
                                      <StarRating rating={book.rating || 5} />
                                    </span>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <div className="text-muted p-3 text-center">
                        No books available
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          {/* DID NOT MODEL */}
          <Modal
            show={showB}
            onHide={handleCloseB}
            centered
            className="custom-modalmeet"
            backdrop={true}
          >
            <Modal.Header className=" border-0">
              <img
                className=" border-0 closebtn "
                src={require("../../components/image/close.png")}
                alt="logo"
                onClick={handleCloseB}
              />
            </Modal.Header>
            <Modal.Title className="text-center mt-sm-3 "></Modal.Title>
            <Modal.Body className="mx-auto mlminmodel mt-sm-3 mt-1">
              <div className="d-flex mt-4 ">
                <div className="text-dark ftzevtt fw-bold fs-5 col-sm-3 col-3 mx-md-5 mx-sm-4 mx-2">
                  Book’s Cover:
                </div>
                <div>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    accept="image/*"
                    id="fileInput"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                  />

                  <button
                    className=" fw600"
                    style={{
                      fontSize: "18px",
                      width: "151px",
                      height: "38px",
                      border: "2px solid #34C3C9",
                      color: "#5B5B5B",

                      cursor: "pointer",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => document.getElementById("fileInput").click()}
                  >
                    {imageName
                      ? imageName.substring(0, 7) +
                        (imageName.length > 7 ? "..." : "")
                      : "Upload"}
                  </button>
                  {/* {imageName && <p style={{ marginTop: "10px" }}>📁 {imageName}</p>} */}
                  {/* Image Preview */}
                  {selectedImagemodel && (
                    <div className="d-none" style={{ marginTop: "10px" }}>
                      <img
                        src={selectedImagemodel}
                        alt="Preview"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                </div>
                <div
                  className="fw600 ftzevtt d-sm-block d-none mx-md-5 mx-3 my-1"
                  style={{ color: "#2B2B2B", fontSize: "18px" }}
                >
                  (Optional)
                </div>
              </div>

              <div className="d-flex mt-3 ">
                <div className="text-dark fw-bold col-sm-3  col-3 ftzevtt fs-5 mx-md-5 mx-sm-4 mx-2">
                  Title:
                </div>

                <div className="">
                  <input
                    type="text"
                    className="form-control modeltextwid widmeetupin fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="text"
                    value={modalData.title}
                    name="title"
                    onChange={handleModalInputChange}
                    required
                    style={{
                      border: "2px solid #3C3C3C",
                      width: "388px",
                      height: "38px",
                    }}
                  />
                </div>
              </div>

              <div className="d-flex mt-3 ">
                <div className="text-dark ftzevtt fw-bold col-sm-3 col-3 fs-5 mx-md-5 mx-sm-4 mx-2">
                  Writer:
                </div>

                <div className="">
                  <input
                    type="text"
                    value={modalData.writer}
                    name="writer"
                    onChange={handleModalInputChange}
                    className="form-control modeltextwid widmeetupin  fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="text"
                    required
                    style={{
                      border: "2px solid #3C3C3C",
                      width: "388px",
                      height: "38px",
                    }}
                  />
                </div>
              </div>

              <div className="d-flex mt-3 ">
                <div className="text-dark fw600 ftzevtt col-sm-3 col-3 fs-5 mx-md-5 mx-sm-4 mx-2">
                  Genre:
                </div>

                <div className="">
                  <Form.Select
                    className="form-control modeltextwid  widmeetupin text-dark fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="visibilitySelect"
                    // value={visibility}
                    // onChange={(e) => setVisibility(e.target.value)}
                    style={{
                      border: "2px solid #3C3C3C",
                      width: "388px",
                      // height: "40px",
                    }}
                    value={modalData.genre}
                    name="genre"
                    onChange={handleModalInputChange}
                  >
                    <option value="Genre">Genre</option>
                    <option value="Crime">Crime</option>
                    <option value="Fiction">Fiction</option>
                    <option value="Graphic Novels & Manga">
                      Graphic Novels & Manga
                    </option>
                    <option value="Non-Fiction">Non-Fiction</option>
                    <option value="Sci-Fi & Fantasy">Sci-Fi & Fantasy</option>
                    <option value="Young Adults & LGBTQ+">
                      Young Adults & LGBTQ+
                    </option>
                  </Form.Select>
                </div>
              </div>

              <div className="d-flex mt-3 ">
                <div className="text-dark fw600 ftzevtt col-sm-3 col-3 fs-5 mx-md-5 mx-sm-4 mx-2">
                  About the book:
                </div>

                <div className="">
                  <textarea
                    className="form-control modeltextwid widmeetupin fw600 fs-5 ftzevtt rounded-1 placeholder-login"
                    id="aboutBook"
                    value={modalData.aboutBook}
                    name="aboutBook"
                    onChange={handleModalInputChange}
                    rows="4"
                    style={{
                      border: "2px solid #3C3C3C",
                      width: "388px",
                      resize: "vertical",
                    }}
                    placeholder="Enter a description of the book..."
                  />
                </div>
              </div>
              <div className="d-flex mladd justify-content-center mt-4">
                <button
                  className="text-white subbtnmeet border-0 fs-18 fw600 rounded-1"
                  style={{
                    width: "135px",
                    height: "44px",
                    backgroundColor: "#62C0C7",
                  }}
                  onClick={addBookManually}
                >
                  Add
                </button>
              </div>
            </Modal.Body>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default SuggestiveBookPage;

